import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { TERM_DEFS, TERM_SLUGS } from "@/data/glossary";

/**
 * Inline term rendered as an anchor to /learn#slug with a hover/tap preview.
 * - Desktop: hover shows a dark tooltip with the one-line definition;
 *   clicking navigates to the Learn page anchor.
 * - Mobile: tapping navigates directly to the Learn page anchor (simpler).
 * The tooltip is viewport-aware: it flips above the trigger when near the
 * bottom edge, and clamps horizontally so it never overflows left/right.
 */
export const TermTip = ({ term }) => {
  const [open, setOpen] = useState(false);
  // Fixed-positioned tooltip coords + flip flag.
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "bottom" });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const tipRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Recompute position whenever the tooltip opens (or on scroll/resize).
  useLayoutEffect(() => {
    if (!open) return;

    const compute = () => {
      const trigger = triggerRef.current;
      const tip = tipRef.current;
      if (!trigger || !tip) return;

      const trigRect = trigger.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 8; // gap between trigger and tooltip

      // Decide vertical placement: prefer below, flip above if not enough room.
      const spaceBelow = vh - trigRect.bottom;
      const placement =
        spaceBelow < tipRect.height + margin + 8 && trigRect.top > tipRect.height + margin + 8
          ? "top"
          : "bottom";

      const top =
        placement === "bottom"
          ? trigRect.bottom + margin
          : trigRect.top - tipRect.height - margin;

      // Horizontal: center on trigger, then clamp within viewport.
      const rawLeft = trigRect.left + trigRect.width / 2 - tipRect.width / 2;
      const left = Math.max(8, Math.min(rawLeft, vw - tipRect.width - 8));

      setPos({ top, left, placement });
    };

    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  const def = TERM_DEFS[term];
  const slug = TERM_SLUGS[term];
  if (!def || !slug) return <span>{term}</span>;

  return (
    <span ref={wrapRef} className="relative inline-block align-baseline">
      <Link
        ref={triggerRef}
        to={`/learn#${slug}`}
        data-testid={`term-tip-${term}`}
        aria-describedby={open ? `tip-${term}` : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="border-b border-dotted border-current/50 hover:text-ember focus:text-ember focus:outline-none transition-colors"
        style={{ color: "inherit" }}
      >
        {term}
      </Link>
      {open &&
        createPortal(
          <span
            ref={tipRef}
            id={`tip-${term}`}
            role="tooltip"
            data-placement={pos.placement}
            className="fixed z-[100] w-72 max-w-[calc(100vw-16px)] p-4 rounded-md bg-paper text-char-800 text-xs leading-relaxed border border-char-900/15 shadow-[0_6px_20px_rgba(13,13,13,0.08)] pointer-events-none"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {def}
            <span className="mono-label mt-3 block">
              Read the full definition →
            </span>
          </span>,
          document.body
        )}
    </span>
  );
};

export default TermTip;
